/* @flow */
import styled from "styled-components";
import React, { useEffect, useState } from "react";
import { Checkbox, Loader, Popup, Table } from "semantic-ui-react";
import classnames from "classnames";
import { Description } from "../../components/billing/charge-description";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { fetchEstateCharges } from "../../redux/modules/charge";
import { getById } from "../../redux/restdux/utils";
import { Currency } from "../../containers/common/currency";
import { formatDate } from "../../helpers/date";
import { t } from "../../constant/translations";
import { convertAmountToDefault, getAppCurrency } from "../../helpers/currency";

type Props = {
  estate: Object,
  chargeQ: Object,
  charges: Array<any>,
  fetchCharges: Function,
  onSelect: Function,
  loading: boolean,
  selectableRows?: boolean,
  amountField?: string,
};

type State = {
  selected: Set<any>,
};

const StyledAmountSpan = styled.span`
  font-weight: bold;
  text-align: right;
`;

const StyledTableRow = styled(Table.Row)`
  &.rowSelected {
    background-color: ${(props) => props.theme.rowSelected};
    color: #fff;

    a,
    a:hover {
      color: #fff !important;
    }

    transition: background-color 0.3s ease;
  }
`;

const Totals = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin: 0 10px 10px 0;
`;

export const ChargeListSmallComponent = ({ chargeQ = {}, ...props }: Props) => {
  const [selected, setSelected] = useState<Set<any>>(() => new Set());

  useEffect(() => {
    const query = {
      page_size: chargeQ.page_size || 300,
      status: chargeQ.status || "unsettled",
    };

    if (props.fetchCharges) {
      props.fetchCharges(props.estate.id, query);
    }
  }, []);

  useEffect(() => {
    setSelected(new Set());
  }, [props.selectableRows]);

  const flipSelected = (charge) => {
    if (!props.selectableRows) {
      return;
    }

    const newSelected = selected;
    if (newSelected.has(charge.id)) {
      newSelected.delete(charge.id);
    } else {
      newSelected.add(charge.id);
    }
    setSelected(newSelected);
    props.onSelect(selected);
  };

  const flipAll = () => {
    if (!props.selectableRows) {
      return;
    }

    const selected = new Set();
    if (!checkAllSelected()) {
      for (const charge of props.charges) {
        selected.add(charge.id);
      }
    }
    setSelected(selected);
    props.onSelect(selected);
  };

  const checkAllSelected = () => {
    if (!props.charges || props.charges.length === 0) {
      return false;
    }

    for (const charge of props.charges) {
      if (!selected.has(charge.id)) {
        return false;
      }
    }

    return true;
  };

  const getTotalSelected = () => {
    let total = 0;
    for (const itemId of selected) {
      const charge = getById(itemId, props.charges);
      total = total + charge[props.amountField];
    }
    if (total === 0) {
      return null;
    }

    return total;
  };

  const renderChargeList = () => {
    return props.charges.map((charge) => {
      return (
        <StyledTableRow
          key={charge.id}
          onClick={() => flipSelected(charge)}
          className={classnames({
            rowSelected: selected.has(charge.id),
          })}
        >
          {props.selectableRows && (
            <Table.Cell singleLine>
              <Checkbox checked={selected.has(charge.id)} />
            </Table.Cell>
          )}
          {!props.estate && (
            <Table.Cell>{charge.estate_details.name}</Table.Cell>
          )}
          <Table.Cell singleLine>{formatDate(charge.charge_date)}</Table.Cell>
          <Table.Cell singleLine>{charge.fund}</Table.Cell>
          <Table.Cell singleLine textAlign={"right"}>
            <Popup
              position={"left center"}
              hideOnScroll={true}
              hoverable
              flowing
              content={<Description charge={charge} />}
              trigger={
                <a href={"#"}>
                  <Currency amount={charge[props.amountField]} />
                </a>
              }
            />
          </Table.Cell>
        </StyledTableRow>
      );
    });
  };

  if (props.loading) {
    return <Loader active={true} />;
  }
  return (
    <>
      <Table unstackable={true} compact={true}>
        <Table.Header>
          <Table.Row>
            {props.selectableRows && (
              <Table.HeaderCell>
                <Checkbox
                  checked={checkAllSelected()}
                  onClick={flipAll}
                  disabled={!props.selectableRows}
                />
              </Table.HeaderCell>
            )}
            {!props.estate && (
              <Table.HeaderCell>
                {t("resources.estate_capital")}
              </Table.HeaderCell>
            )}
            <Table.HeaderCell>{t("labels.date")}</Table.HeaderCell>
            <Table.HeaderCell>{t("labels.direction")}</Table.HeaderCell>
            <Table.HeaderCell textAlign={"right"}>
              {t("labels.amount")}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>{renderChargeList()}</Table.Body>
      </Table>
      <Totals>
        {getTotalSelected() && (
          <StyledAmountSpan>
            {t("charge.total")}:
            <Currency amount={getTotalSelected()} />
          </StyledAmountSpan>
        )}
      </Totals>
    </>
  );
};

function mapStateToProps(state) {
  const namespace = state.charge.payment || {};
  return {
    charges: namespace.entities,
    loading: !namespace.entities || namespace.pending,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchCharges: (estateId, query) =>
        fetchEstateCharges(estateId, query, "payment"),
    },
    dispatch
  );
}

export const ChargeListSmall = connect(
  mapStateToProps,
  mapDispatchToProps
)(ChargeListSmallComponent);
